import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { SavedCard } from "@models/credit-card";
import { finalize, take } from "rxjs";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { PaymentService } from "../../services/payment.service";
import { SnackBarService } from "../../services/snack-bar.service";
import {
  ConfirmActionModalComponent,
  ConfirmDialogData,
} from "../../shared/modals/confirm-action-modal/confirm-action-modal.component";
import { CreditCardWalletComponent } from "../../shared/components/credit-card-wallet/credit-card-wallet.component";
import { AddCardDialogComponent } from "./add-card-dialog/add-card-dialog.component";

@Component({
  selector: "app-manage-cards",
  imports: [
    CommonModule,
    AppLayoutComponent,
    PageHeaderComponent,
    ButtonComponent,
    CreditCardWalletComponent,
  ],
  templateUrl: "./manage-cards.component.html",
  styleUrl: "./manage-cards.component.scss",
})
export class ManageCardsComponent implements OnInit {
  private readonly paymentService = inject(PaymentService);
  private readonly snackbarService = inject(SnackBarService);
  private readonly matDialog = inject(MatDialog);

  savedCards: SavedCard[] = [];
  loading = false;

  ngOnInit(): void {
    this.loadCards();
  }

  handleAddNewCardClick(): void {
    this.matDialog
      .open(AddCardDialogComponent, {
        width: "min(100vw - 2rem, 32rem)",
        maxWidth: "100vw",
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((saved) => {
        if (saved) this.loadCards();
      });
  }

  handleDeleteCard(card: SavedCard): void {
    this.matDialog
      .open(ConfirmActionModalComponent, {
        width: "min(100vw - 2rem, 26rem)",
        maxWidth: "100vw",
        data: {
          modalTitle: "Remover cartão",
          modalContent: `Tem certeza que deseja remover o cartão final <strong>${card.last_four_digits}</strong>?`,
          confirmMessage: "Sim, remover",
          cancelMessage: "Cancelar",
        } as ConfirmDialogData,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) this.deleteCard(card);
      });
  }

  private deleteCard(card: SavedCard): void {
    this.paymentService
      .deleteCard(card.id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.snackbarService.openSnackBar(
            "Cartão removido.",
            "Fechar",
            "success",
          );
          this.loadCards();
        },
        error: (err) => {
          const message =
            err?.error?.message ||
            err?.error?.error ||
            "Não foi possível remover o cartão.";
          this.snackbarService.openSnackBar(message, "Fechar", "error");
        },
      });
  }

  private loadCards(): void {
    this.loading = true;
    this.paymentService
      .getSavedCards()
      .pipe(
        take(1),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          this.savedCards = response?.cards ?? [];
        },
        error: (error: HttpErrorResponse) => {
          this.savedCards = [];
          if (error.status === 404) return;
          this.snackbarService.openSnackBar(
            "Não foi possível carregar seus cartões.",
            "Fechar",
            "error",
          );
        },
      });
  }
}
